import React, { useState } from "react";
import { TextField, InputAdornment, IconButton, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useFormik } from "formik";
import * as yup from "yup";

import { resetNewPassword, changePasswordAuth } from "../../Actions/AuthActions";
import {
    MyArrowBackIcon,
	MyInfoIcon,
	MyVisibilityIcon,
	MyVisibilityOffIcon,
} from "../../Assets/svg/MyIcons";
import PasswordStrengthRules, { isPasswordValid } from '../../components/Common/PasswordStrengthRules';
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const PASSWORD_REGEX = /^[A-Za-z0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/;

const HtmlTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} arrow />
))(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		minWidth: 600,
		fontSize: "14px",
		border: "1px solid #dadde9",
	},
}));

const PasswordTooltipContent = () => (
	<>
		<Box className="d-flex align-items-center">
			<Typography color="inherit" className="fw-bold me-1">
				1. Length:{" "}
				<Typography color="inherit" component="span">
					Password must be at least 8 characters long.
				</Typography>
			</Typography>
		</Box>
		<Box className="d-flex align-items-center">
			<Typography color="inherit" className="fw-bold">
				2. Complexity:{" "}
				<Typography color="inherit" component="span">
					Include a mix of uppercase and lowercase letters, numbers, and special characters (e.g., !, @, #, $).
				</Typography>
			</Typography>
		</Box>
		<Box className="d-flex align-items-center">
			<Typography color="inherit" className="fw-bold">
				3. Regular Updates:{" "}
				<Typography color="inherit" component="span">
					It's recommended to change your password periodically for added security.
				</Typography>
			</Typography>
		</Box>
	</>
);

const LoadingSpinner = () => (
	<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
);

const passwordValidationSchema = yup.object().shape({
	newPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("new password"))
		.min(8, VALIDATION.MIN_LENGTH("password", 8))
		.max(20, VALIDATION.MAX_LENGTH("password", 20))
		.matches(PASSWORD_REGEX, VALIDATION.PASSWORD_WEAK)
		.test("no-spaces", VALIDATION.PASSWORD_WEAK, (value) => {
			if (!value) return true;
			return !/\s/.test(value);
		}),
	confirmNewPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("confirm password"))
		.oneOf([yup.ref("newPassword"), null], VALIDATION.PASSWORD_MISMATCH),
});

const changePasswordValidationSchema = yup.object().shape({
	oldPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("old password")),
	newPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("new password"))
		.min(8, VALIDATION.MIN_LENGTH("password", 8))
		.max(20, VALIDATION.MAX_LENGTH("password", 20))
		.matches(PASSWORD_REGEX, VALIDATION.PASSWORD_WEAK)
		.test("no-spaces", VALIDATION.PASSWORD_WEAK, (value) => {
			if (!value) return true;
			return !/\s/.test(value);
		})
		.test(
			"not-same-as-old-password",
			"New Password must be different from the Old Password",
			function (value) {
				return value !== this.parent.oldPassword;
			}
		),
	confirmNewPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("confirm password"))
		.oneOf([yup.ref("newPassword"), null], VALIDATION.PASSWORD_MISMATCH),
});

const ResetPassword = ({
	email,
	accessToken,
	ip,
	userDetails,
	showSnackbar,
	currentScreen,
	setCurrentScreen,
	SCREENS,
	isEmailVerified,
	setIsEmailVerified,
}) => {
	const [loading, setLoading] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(false);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
	const [newPasswordFocused, setNewPasswordFocused] = useState(false);
	const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);

	const toggleOldPasswordVisibility = () => setShowOldPassword((prev) => !prev);
	const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);
	const toggleConfirmNewPasswordVisibility = () => setShowConfirmNewPassword((prev) => !prev);

	const isChangePassword = currentScreen === SCREENS.CHANGE_PASSWORD;
	const isSetPassword = currentScreen === SCREENS.SET_PASSWORD;

	const resetPasswordFormik = useFormik({
		initialValues: { newPassword: "", confirmNewPassword: "" },
		validationSchema: passwordValidationSchema,
		onSubmit: handleResetPassword,
	});

	const changePasswordFormik = useFormik({
		initialValues: { oldPassword: "", newPassword: "", confirmNewPassword: "" },
		validationSchema: changePasswordValidationSchema,
		onSubmit: handleChangePassword,
	});

	async function handleResetPassword(values) {
		if (!isPasswordValid(values.newPassword)) {
			showSnackbar("Password does not meet all requirements", "error");
			return;
		}
		setButtonDisable(true);
		setLoading(true);

		try {
			const data = { ...values, token: accessToken, ip };

			if (isSetPassword) {
				data.email = userDetails?.data?.email;
				delete data.token;
			}

			const res = await resetNewPassword(data);
			showSnackbar(res.data.message, "success");

			setIsEmailVerified(false);

			setTimeout(() => {
				setCurrentScreen(SCREENS.LOGIN);
			}, 1000);
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setLoading(false);
			setButtonDisable(false);
		}
	}

	async function handleChangePassword(values) {
		if (!isPasswordValid(values.newPassword)) {
			showSnackbar("Password does not meet all requirements", "error");
			return;
		}
		setButtonDisable(true);
		setLoading(true);

		try {
			const res = await changePasswordAuth({ ...values, email });
			showSnackbar(res.data.message, "success");
			changePasswordFormik.resetForm();

			setIsEmailVerified(false);

			setTimeout(() => {
				setCurrentScreen(SCREENS.LOGIN);
			}, 1000);
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setLoading(false);
			setButtonDisable(false);
		}
	}

	const formik = isChangePassword ? changePasswordFormik : resetPasswordFormik;

	const getButtonDisabled = () => {
		if (isChangePassword) {
			return (
				buttonDisable ||
				loading ||
				!changePasswordFormik.values.oldPassword ||
				!changePasswordFormik.values.newPassword ||
				!changePasswordFormik.values.confirmNewPassword ||
				!!changePasswordFormik.errors.oldPassword ||
				!!changePasswordFormik.errors.newPassword ||
				!!changePasswordFormik.errors.confirmNewPassword ||
				!isPasswordValid(changePasswordFormik.values.newPassword)
			);
		}
		return (
			buttonDisable ||
			loading ||
			!resetPasswordFormik.values.newPassword ||
			!resetPasswordFormik.values.confirmNewPassword ||
			!!resetPasswordFormik.errors.newPassword ||
			!!resetPasswordFormik.errors.confirmNewPassword ||
			!isPasswordValid(resetPasswordFormik.values.newPassword)
		);
	};

	const getButtonText = () => {
		if (isChangePassword) return "Change Password";
		if (isSetPassword) return "Set Password";
		return "Set Password";
	};

	// const getTitle = () => {
	// 	if (isChangePassword) return "Change Password";
	// 	if (isSetPassword) return "Set Password";
	// 	return null;
	// };

	const handleBack = () => {
		formik.resetForm();
		setCurrentScreen(SCREENS.LOGIN);
	};

	const renderPasswordField = (name, label, placeholder, showPassword, toggleVisibility, formikInstance, extraProps = {}) => (
		<TextField
			name={name}
			label={label}
			placeholder={placeholder}
			type={showPassword ? "text" : "password"}
			value={formikInstance.values[name]}
			onChange={formikInstance.handleChange}
			onBlur={formikInstance.handleBlur}
			error={formikInstance.touched[name] && Boolean(formikInstance.errors[name])}
			helperText={formikInstance.touched[name] && formikInstance.errors[name]}
			fullWidth
			variant="outlined"
			className="form-control fs-5 bg-transparent"
			{...extraProps}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end" className="gap-2">
						<IconButton onClick={toggleVisibility} edge="end">
							{showPassword ? <MyVisibilityOffIcon /> : <MyVisibilityIcon />}
						</IconButton>
						<HtmlTooltip title={<PasswordTooltipContent />}>
							<IconButton>
								<MyInfoIcon />
							</IconButton>
						</HtmlTooltip>
					</InputAdornment>
				),
			}}
		/>
	);

	if (isChangePassword) {
		return (
			<>
				<div className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"oldPassword",
						null,
						"Old Password",
						showOldPassword,
						toggleOldPasswordVisibility,
						changePasswordFormik
					)}
				</div>

				<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
					{renderPasswordField(
						"newPassword",
						null,
						"New Password",
						showNewPassword,
						toggleNewPasswordVisibility,
						changePasswordFormik,
						{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
					)}
					{newPasswordFocused && (
						<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
							<PasswordStrengthRules password={formik.values.newPassword} show={true} />
						</Box>
					)}
				</div>

				<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
					{renderPasswordField(
						"confirmNewPassword",
						null,
						"Confirm New Password",
						showConfirmNewPassword,
						toggleConfirmNewPasswordVisibility,
						changePasswordFormik,
						{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
					)}
					{confirmPasswordFocused && (
						<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
							<PasswordStrengthRules password={formik.values.confirmNewPassword} show={true} />
						</Box>
					)}
				</div>

				<div className="d-grid">
					<button
						type="submit"
						className="btn btn-primary fs-3 h-50px mt-2"
						disabled={getButtonDisabled()}
						onClick={changePasswordFormik.handleSubmit}
					>
						{getButtonText()}
						{loading && <LoadingSpinner />}
					</button>
				</div>

				<div className="text-center mt-6">
					<button
						type="button"
						onClick={handleBack}
						className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
					>
						<MyArrowBackIcon className="back-arrow me-2" />
						<span>Back to Login</span>    
					</button>
				</div>
			</>
		);
	}

	if (isSetPassword) {
		return (
			<>
				<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
					{renderPasswordField(
						"newPassword",
						null,
						"New Password",
						showNewPassword,
						toggleNewPasswordVisibility,
						resetPasswordFormik,
						{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
					)}
					{newPasswordFocused && (
						<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
							<PasswordStrengthRules password={formik.values.newPassword} show={true} />
						</Box>
					)}
				</div>

				<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
					{renderPasswordField(
						"confirmNewPassword",
						null,
						"Confirm New Password",
						showConfirmNewPassword,
						toggleConfirmNewPasswordVisibility,
						resetPasswordFormik,
						{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
					)}
					{confirmPasswordFocused && (
						<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
							<PasswordStrengthRules password={formik.values.confirmNewPassword} show={true} />
						</Box>
					)}
				</div>

				<div className="d-grid">
					<button
						type="submit"
						className="btn btn-primary fs-3 h-50px mt-2"
						disabled={getButtonDisabled()}
						onClick={resetPasswordFormik.handleSubmit}
					>
						{getButtonText()}
						{loading && <LoadingSpinner />}
					</button>
				</div>

				<div className="text-center mt-6">
					<button
						type="button"
						onClick={handleBack}
						className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
					>
						<MyArrowBackIcon className="back-arrow me-2" />
						<span>Back to Login</span>    
					</button>
				</div>
			</>
		);
	}

	return (
		<>
			<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
				{renderPasswordField(
					"newPassword",
					null,
					"New Password",
					showNewPassword,
					toggleNewPasswordVisibility,
					resetPasswordFormik,
					{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
				)}
				{newPasswordFocused && (
					<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
						<PasswordStrengthRules password={formik.values.newPassword} show={true} />
					</Box>
				)}
			</div>

			<div className="fv-row mb-6 fv-plugins-icon-container" style={{ position: 'relative' }}>
				{renderPasswordField(
					"confirmNewPassword",
					null,
					"Confirm New Password",
					showConfirmNewPassword,
					toggleConfirmNewPasswordVisibility,
					resetPasswordFormik,
					{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
				)}
				{confirmPasswordFocused && (
					<Box sx={{ position: 'absolute', left: '102%', top: '-2rem', zIndex: 9999, width: '300px' }}>
						<PasswordStrengthRules password={formik.values.confirmNewPassword} show={true} />
					</Box>
				)}
			</div>

			<div className="d-grid">
				<button
					type="submit"
					className="btn btn-primary fs-3 h-50px mt-2"
					disabled={getButtonDisabled()}
					onClick={resetPasswordFormik.handleSubmit}
				>
					{getButtonText()}
					{loading && <LoadingSpinner />}
				</button>
			</div>

			<div className="text-center mt-6 pe-4">
					<button
						type="button"
						onClick={handleBack}
						className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
				>
						<MyArrowBackIcon className="back-arrow me-2" />
						<span>Back to Login</span>    
					</button>
			</div>
		</>
	);
};

export default ResetPassword;